import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DemoModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", academy: "", phone: "", team_size: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/demo-request`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError("");
    setForm({ name: "", email: "", academy: "", phone: "", team_size: "" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-600 rounded-sm flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-black text-gray-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              NextTackle
            </span>
          </div>
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Request a Demo
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-14 h-14 text-green-600 mx-auto mb-4" />
            <h3
              className="text-lg font-bold text-gray-900 mb-2"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Request Received!
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              Our team will contact you within 24 hours to schedule your personalized demo.
            </p>
            <Button
              onClick={handleClose}
              className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
              data-testid="demo-success-close-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <p className="text-sm text-gray-600">
              Fill in your details and we'll set up a personalized demo for your academy.
            </p>
            <div>
              <Label htmlFor="demo-name">Full Name *</Label>
              <Input
                id="demo-name"
                value={form.name}
                onChange={update("name")}
                placeholder="John Smith"
                required
                data-testid="demo-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="demo-email">Work Email *</Label>
              <Input
                id="demo-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="john@academy.com"
                required
                data-testid="demo-email-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="demo-academy">Academy Name *</Label>
              <Input
                id="demo-academy"
                value={form.academy}
                onChange={update("academy")}
                placeholder="Elite FC Academy"
                required
                data-testid="demo-academy-input"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="demo-phone">Phone</Label>
                <Input
                  id="demo-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+44 20 1234 5678"
                  data-testid="demo-phone-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="demo-team-size">Team Size</Label>
                <Input
                  id="demo-team-size"
                  value={form.team_size}
                  onChange={update("team_size")}
                  placeholder="e.g. 25 players"
                  data-testid="demo-team-size-input"
                  className="mt-1"
                />
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-600" data-testid="demo-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 text-white rounded-sm"
              data-testid="demo-submit-btn"
            >
              {loading ? "Sending..." : "Request Demo"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
